import React, { ReactElement, useContext } from 'react'
import { EntryContext } from '../contexts/EntryContext'
import { EntryRetriever } from '../reducers/EntryReducer'
import { Span } from './Entry.d'
import { faCalendarWeek, faIndent, faListOl, faSquareCaretDown, faSquareCaretRight, faStar
} from '@fortawesome/free-solid-svg-icons'
import { IconProp, library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { formatDate } from '../helpers/format'
import { AppContext } from '../contexts/AppContext'
import { ReloadButton } from '../common/ReloadButton'
library.add(faSquareCaretDown, faSquareCaretRight, faIndent, faListOl, faCalendarWeek, faStar)

const TOP_LEVEL_SPAN_ID = 0
const isSpanCollapsed = (span: Span): boolean => span.collapsed !== undefined ? span.collapsed : !span.spansNow
const ONE_DAY_SECONDS = 24 * 60 * 60

const EntrySpans = (): ReactElement => {
    const { state } = useContext(EntryContext)
    return <>
        <h3>Timespan entries { state.spans && <ReloadButton context={ EntryContext } action={ { type: 'RETRIEVE_SPANS' } } /> }
        </h3>
        <div className="tip">
            Here you can view all <FontAwesomeIcon icon="calendar-week" /> span-type entries.
        </div>
        <EntryRetriever>
            <EntrySpansContent entryId={ TOP_LEVEL_SPAN_ID } />
        </EntryRetriever>
    </>
}

export const EntrySpansContent = (props: { entryId: number }): ReactElement => {
    const { state } = useContext(EntryContext)
    const span = state.spans?.[props.entryId]
    if (span === undefined) {
        return <></>
    }
    return <div className={ span.id === TOP_LEVEL_SPAN_ID ? undefined : 'entry-span' }>
        { props.entryId !== TOP_LEVEL_SPAN_ID && <EntrySpan entryId={ props.entryId } />}
        <div className={ 'entry-span-children' + (isSpanCollapsed(span) ? ' entry-span-collapsed' : '') }>
            {span.immediateChildren.map(entryId => <EntrySpansContent key={ entryId } entryId={ entryId } />)}
        </div>
    </div>
}

const SpanButton = (props: {
    value: number | boolean
    icon: IconProp
    className?: string
    description?: string
    onClick?: () => void
}): ReactElement => {
    const { appDispatch } = useContext(AppContext)
    if (!props.value) {
        return <></>
    }
    const message = `${props.description || ''}${typeof props.value !== 'boolean' ? `: ${props.value}` : ''}`
    return <span
        onMouseOver={ () => appDispatch({ type: 'SET_NOTIFICATION_TEXT', message, icon: props.icon }) }
        onMouseOut={ () => appDispatch({ type: 'CLEAR_NOTIFICATION_TEXT', message }) }>
        <FontAwesomeIcon
            onClick={ props.onClick }
            icon={ props.icon }
            className={ props.className }
        />{typeof props.value !== 'boolean' ? ` ${props.value}` : ''}
    </span>
}

export const entrySpanDates = (span: Span): string =>
    formatDate(span.startTimestamp * 1000, 'long')
    + ((!!span.isSpan && span.endTimestamp >= span.startTimestamp + ONE_DAY_SECONDS)
        ? ' to ' + (span.spansNow
            ? 'now'
            : formatDate(span.endTimestamp * 1000, 'long'))
        : '')

const EntrySpan = (props: { entryId: number }): ReactElement => {
    const { appDispatch } = useContext(AppContext)
    const { state, dispatch } = useContext(EntryContext)
    const span = state.spans?.[props.entryId]
    if (span === undefined) {
        return <></>
    }
    const updatedSpan: { title?: string } = state.write?.[props.entryId] || state.entries?.[props.entryId] || {}
    const dateString = entrySpanDates(span)
    const title = updatedSpan.title || span.title
    return <>
        <FontAwesomeIcon icon={ span.isSpan ? 'calendar-week' : 'star' } />{' '}
        <Link to={ '/write?entryId=' + props.entryId }>{title || dateString}</Link>
        <SpanButton
            value={ span.numInnerImportant }
            icon="star"
            description='Number of very highly-rated or "important" entries within this time period' />
        <SpanButton
            value={ span.numInnerSpans }
            icon="calendar-week"
            description='Number of further "span" entries within this one' />
        <Link
            to={ `/people?spanId=${props.entryId}` }
            onClick={ () => {
                appDispatch({ type: 'CLEAR_NOTIFICATION_TEXT', message: 'Explore this span with more statistics and graphs' })
                appDispatch({ type: 'EXPLORE_SELECT_TAB', tab: 'recent' })
            } }
        >{' '}
            <SpanButton
                value={ span.isSpan > 0 }
                className="clickable"
                icon="list-ol"
                description="Explore this span with more statistics and graphs" />
        </Link>
        <SpanButton
            value={ span.immediateChildren.length > 0 }
            className="clickable"
            onClick={ () => dispatch({ type: 'ENTRY_SPAN_TOGGLE', entryId: span.id }) }
            icon={ isSpanCollapsed(span) ? 'square-caret-right' : 'square-caret-down' } />
        { title && <p className="entry-span-dates">{dateString}</p>}
    </>
}

export default EntrySpans
